import { Print } from "@mui/icons-material";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from "@mui/material";
import { LogOutIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MessageDialog from "../../common/dialog/commonDialogBox";
import {
  fetchAdmissionPatient,
  fetchAllAdmissions,
  updateAdmission
} from "../../redux/admissions/admissionsActions";
import { addAttender, resetCurrentAttender } from "../../redux/Attender/attenderAction";
import AdmissionsTable from "./subcomponent/AdmissionsTable";
import AttenderFormDrawer from "./subcomponent/AttenderFormDrawer";
import { handlePrintAttenderPasses } from "./subcomponent/generateQRData";
import PrintAttenderDialog from "./subcomponent/PrintAttenderDialog";

const AttenderAdd = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAllAdmissions || {}
  );

  const attenderAdd = useSelector(
    (state) => state?.attender?.addAttenders || {}
  );
   const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [attendersList, setAttendersList] = useState([{ id: Date.now() }]);
  const [selectedAttenders, setSelectedAttenders] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  
  // Discharge state management
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const totalPages =admissionsList?.admissions?.totalPages || 0

    const fetchData = async (page) => {
      await dispatch(fetchAllAdmissions(page));
      setLoading(false);
    };

  useEffect(() => {
      fetchData(currentPage);
    }, [currentPage, dispatch]);
  useEffect(() => {
    if (attenderAdd.success) {
      setDrawerOpen(false);
      setMessageDialog({
        open: true,
        title: "Success",
        message: "Attender added successfully!",
        type: "success",
      });
      dispatch(resetCurrentAttender())
      fetchData(currentPage);
    } else if (attenderAdd.error) {
      setMessageDialog({
        open: true,
        title: "Error",
        message: attenderAdd.message || "Failed to add attender.",
        type: "error",
      });
    }
  }, [attenderAdd]);
  const handleDischargeClick = (row) => {
    if (row.statusCode === 1) {
      setSelectedAdmission(row);
      setDischargeDialogOpen(true);
    }
  };

const handleDischargeConfirm = async () => {
  if (selectedAdmission) {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(
      2,
      "0"
    )} ${String(currentDate.getHours()).padStart(2, "0")}:${String(
      currentDate.getMinutes()
    ).padStart(2, "0")}`;
    
    const formdata = {
      admission_status: "3",
      discharge_date: formattedDate,
    };

    try {
   
      await dispatch(updateAdmission(selectedAdmission.id, formdata));
      setDischargeDialogOpen(false);
      setSuccessSnackbarOpen(true);
      fetchData(currentPage);
    } catch (error) {
      console.error("Error discharging patient:", error);
  
    }
  }
};

  const handleDischargeCancel = () => {
    setDischargeDialogOpen(false);
    setSelectedAdmission(null);
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false);
  };

  const columns = [
    { id: "admissionNo", label: "Admission No", sortable: true },
    { id: "patientSerialNo", label: "Patient ID", sortable: true },
    { id: "patientName", label: "Patient Name", sortable: true },
    { id: "ipNumber", label: "IP Number", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    {
      id: "admitDate",
      label: "Admit Date",
      sortable: true,
      render: (value) => {
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    {
      id: "dischargeDate",
      label: "Discharge Date",
      sortable: true,
      render: (value) => {
        if (!value) return "Not Discharged";
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    { id: "departmentName", label: "Department", sortable: true },
    { id: "wardName", label: "Ward", sortable: true },
    { id: "numAttenders", label: "Attenders", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "In Active"
              ? "default"
              : value === "Discharged"
              ? "primary"
              : "warning"
          }
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      render: (_, row) => (
        <Box sx={{ display: "flex", gap: 5 }}>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => handleOpenAttenderForm(row)}
            >
              Add Attender
            </Button>
          </Box>
      
          {row.statusCode === 1 && (
            <Box>
              <Button
                onClick={() => handleDischargeClick(row)}
                size="small"
                title="Discharge"
                startIcon={
                  <LogOutIcon sx={{ color: "red" }} />
                }
                sx={{ color: "red" }}
              >
                Discharge
              </Button>
            </Box>
          )}
      
          {row.attenders && row.attenders.length > 0 && (
            <Box>
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<Print />}
                onClick={() => handleOpenPrintDialog(row)}
                sx={{
                  bgcolor: "#2196f3",
                  color: "white",
                  "&:hover": { bgcolor: "#1976d2" },
                }}
              >
                Print Passes
              </Button>
            </Box>
          )}
        </Box>
      )
      
    },
  ];

 

  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.id,
        admissionNo: admission.admission_no,
        patientId: admission.Patient?.id,
        patientSerialNo: admission.Patient?.patient_serial_no,
        patientName: admission.Patient?.patient_name,
        ipNumber: admission.Patient?.ip_number,
        phone: admission.Patient?.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockId: admission.Block?.id || "N/A",
        Zone: admission.Block?.zone_name || "N/A",
        floorId: admission.Floor?.id || "N/A",
        blockName: admission.Block?.block_name || "N/A",
        floorName: admission.Floor?.floor_name || "N/A",
        departmentName: admission.Department?.department_name || "N/A",
        wardName: admission.Ward?.ward_name || "N/A",
        wardId: admission.Ward?.id || "N/A",
        wardType: admission.Ward?.ward_type || "N/A",
        status: getStatusLabel(admission.admission_status),
        statusCode: parseInt(admission.admission_status),
        numAttenders: admission.number_attenders || 0,
        attenders: admission.Attenders || [],
      }));
      setData(formattedData);
    } else {
      setData([]);
    }
  }, [admissionsList]);

  const getStatusLabel = (statusCode) => {
    switch (parseInt(statusCode)) {
      case 1:
        return "Active";
      case 2:
        return "In Active";
      case 3:
        return "Discharged";
      default:
        return "Unknown";
    }
  };

  const handleOpenAttenderForm = (row) => {
    setSelectedPatient(row);
    setAttendersList([{ id: Date.now() }]);
    setDrawerOpen(true);
  };

  const handleOpenPrintDialog = (row) => {
    setSelectedPatient(row);
    setSelectedAttenders([]);
    setPrintDialogOpen(true);
  };

  const handleDialogClose = () => {
    setMessageDialog((prev) => ({ ...prev, open: false }));
  };

  const handleAddAttender = () => {
    setAttendersList([...attendersList, { id: Date.now() }]);
  };

  const handleRemoveAttender = (id) => {
    if (attendersList.length > 1) {
      setAttendersList(attendersList.filter((item) => item.id !== id));
    }
  };

  const onSubmitAttenders = async (formData) => {
    try {
      setSubmitting(true);
      const attenderDetails = attendersList.map((item, index) => ({
        attender_name: formData[`attender_name_${index}`],
        relation: formData[`relation_${index}`],
        phone: formData[`phone_${index}`],
        address: formData[`address_${index}`],
      }));

      const payload = {
        patient_id: selectedPatient.patientId,
        admission_id: selectedPatient.id,
        block_id: selectedPatient.blockId,
        ward_id: selectedPatient.wardId,
        floor_id: selectedPatient.floorId,
        ward_type: selectedPatient.wardType || "",
        // wardName:selectedPatient.Ward.ward_name || "",
        attender_details: attenderDetails,
      };

      const response = await dispatch(addAttender(payload));
      if (response?.payload?.status === 200 || attenderAdd?.status === 200) {
        fetchData(currentPage);
        await dispatch(fetchAdmissionPatient(selectedPatient.id));
        await handlePrintAttenderPasses({
          selectedPatient,
          attendersList,
          submitting,
          selectedAttenders: []
        });

        setTimeout(() => {
          setDrawerOpen(false);
          setSubmitting(false);
        }, 500);
      } else {
        console.error(
          "Error adding attenders:",
          response?.payload?.message || "Unknown error"
        );
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Error adding attenders:", error);
      setSubmitting(false);
    }
    fetchData(currentPage);
  };
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrintSelected = async () => {
    if (selectedAttenders.length === 0) {
      return;
    }

    await handlePrintAttenderPasses({
      selectedPatient,
      attendersList: [],
      submitting: false,
      selectedAttenders
    });
    
    setPrintDialogOpen(false);
  };

  return (
    <Box>
      <AdmissionsTable 
        columns={columns}
        data={data}
        isLoading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      <AttenderFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedPatient={selectedPatient}
        attendersList={attendersList}
        handleAddAttender={handleAddAttender}
        handleRemoveAttender={handleRemoveAttender}
        onSubmit={onSubmitAttenders}
        submitting={submitting}
      />

      <PrintAttenderDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        selectedPatient={selectedPatient}
        selectedAttenders={selectedAttenders}
        setSelectedAttenders={setSelectedAttenders}
        handlePrintSelected={handlePrintSelected}
      />

      <MessageDialog
        open={messageDialog.open}
        onClose={handleDialogClose}
        title={messageDialog.title}
        message={messageDialog.message}
        type={messageDialog.type}
      />

      {/* Discharge Confirmation Dialog */}
      <Dialog
        open={dischargeDialogOpen}
        onClose={handleDischargeCancel}
        aria-labelledby="discharge-dialog-title"
      >
        <DialogTitle
          id="discharge-dialog-title"
          sx={{ bgcolor: "red", color: "white" }}
        >
          Confirm Discharge
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Are you sure you want to discharge this patient?
            {selectedAdmission && (
              <Box component="span" fontWeight="bold">
                {" "}
                {selectedAdmission.patientName}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDischargeCancel}
            sx={{
              backgroundColor: "orange",
              color: "white",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDischargeConfirm}
            variant="contained"
            sx={{
              backgroundColor: "green",
              color: "white",
            }}
          >
            Discharge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: "100%", bgcolor: "green", color: "white" }}
        >
          Patient discharged successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AttenderAdd;